import React, { useEffect } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import RemoteVizApp from "@/components/RemoteVizApp";
import HistoricTargetView from "@/components/chat/HistoricTargetView";
import { ChatProvider, useChat } from "@/context/ChatContext";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/ui/spinner";
import SEO from "@/components/SEO";

// Session handler for active chat (NOT historic views)
const SessionHandler = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { switchSession, createNewSession } = useChat();
  const sessionId = searchParams.get('session');
  const newChat = searchParams.get('newChat');
  const [isHandled, setIsHandled] = React.useState(false);

  useEffect(() => {
    if (isHandled) return;

    const handleSessionParam = async () => {
      if (sessionId) {
        console.log('🏠 Index: Loading session from URL:', sessionId);
        setIsHandled(true);
        
        try {
          await switchSession(sessionId);
          console.log('✅ Index: Session loaded successfully from History');
        } catch (error) {
          console.error('❌ Index: Failed to load session from URL:', error);
        } finally {
          setSearchParams({});
        }
      } else if (newChat === 'true') {
        console.log('🏠 Index: Creating new session from navigation');
        setIsHandled(true);
        
        try {
          await createNewSession();
          console.log('✅ Index: New session created successfully');
        } catch (error) {
          console.error('❌ Index: Failed to create new session from navigation:', error);
        } finally {
          setSearchParams({});
        }
      }
    };

    handleSessionParam();
  }, [sessionId, newChat, isHandled]);

  return <RemoteVizApp />;
};

const Index = () => {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Check for historic target BEFORE rendering ChatProvider
  const targetId = searchParams.get('target');
  const targetType = searchParams.get('type');
  const returnPage = searchParams.get('page');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  // Redirect unauthenticated users to landing page
  if (!user) {
    return <Navigate to="/landing" replace />;
  }

  // If viewing a historic target, render DIRECTLY without ChatProvider
  // This prevents unnecessary session initialization
  if (targetId) {
    console.log('🏠 Index: Rendering HistoricTargetView directly (no ChatProvider)');
    return (
      <HistoricTargetView 
        targetId={targetId}
        targetType={targetType as 'location' | 'image' | 'prediction' | null}
        returnPage={parseInt(returnPage || '1', 10)}
      />
    );
  }

  // Only wrap RemoteVizApp with ChatProvider for active chat
  return (
    <>
      <SEO
        title="RemoteViz.ai — Practice Remote Viewing with AI Feedback"
        description="Start a remote viewing session, log impressions, and get instant AI feedback on locations, images, and prediction targets."
        path="/"
      />
      <ChatProvider>
        <SessionHandler />
      </ChatProvider>
    </>
  );
};

export default Index;